import React, { useState, useEffect, useRef } from 'react';
import { DollarSign, Search, Save, Filter, RefreshCw, Check, X, AlertCircle, Percent } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import apiService from '../../services/apiService';
import Button from '../../components/ui/Button';
import './CambioPreciosPage.css';

const CambioPreciosPage = () => {
  const { showToast, loading, setLoading } = useApp();

  // Filtros
  const [categorias, setCategorias] = useState([]);
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [busqueda, setBusqueda] = useState('');

  // Productos
  const [productos, setProductos] = useState([]);
  const [productosEditados, setProductosEditados] = useState({});
  const [porcentajes, setPorcentajes] = useState({}); // Porcentajes temporales (no se guardan)
  const [guardando, setGuardando] = useState(false);

  // Filtros de columna tipo Excel
  const [filtrosColumna, setFiltrosColumna] = useState({
    codigo: '',
    nombre: ''
  });

  // Carga progresiva
  const [offset, setOffset] = useState(0);
  const [hayMas, setHayMas] = useState(true);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [totalProductos, setTotalProductos] = useState(0);
  const limite = 50;

  // Ref para trigger de búsqueda
  const busquedaTrigger = useRef(0);

  // Ref para debounce del filtro de nombre
  const filtroNombreTimeout = useRef(null);
  const [buscandoNombre, setBuscandoNombre] = useState(false);

  // Refs para scroll sincronizado
  const tablaContainerRef = useRef(null);
  const topScrollRef = useRef(null);
  const tablaRef = useRef(null);

  // Cargar categorías al montar
  useEffect(() => {
    cargarCategorias();
  }, []);

  // Sincronizar scroll superior e inferior
  useEffect(() => {
    const tablaContainer = tablaContainerRef.current;
    const topScroll = topScrollRef.current;

    if (!tablaContainer || !topScroll) return;

    const syncScrollFromTable = () => {
      topScroll.scrollLeft = tablaContainer.scrollLeft;
    };

    const syncScrollFromTop = () => {
      tablaContainer.scrollLeft = topScroll.scrollLeft;
    };

    tablaContainer.addEventListener('scroll', syncScrollFromTable);
    topScroll.addEventListener('scroll', syncScrollFromTop);

    return () => {
      tablaContainer.removeEventListener('scroll', syncScrollFromTable);
      topScroll.removeEventListener('scroll', syncScrollFromTop);
    };
  }, []);

  // Actualizar ancho del scroll superior cuando cambian los productos
  useEffect(() => {
    if (tablaRef.current && topScrollRef.current) {
      const scrollContent = topScrollRef.current.querySelector('.top-scroll-content');
      if (scrollContent) {
        scrollContent.style.width = `${tablaRef.current.scrollWidth}px`;
      }
    }
  }, [productos]);

  // Cargar productos cuando cambian los filtros
  useEffect(() => {
    // Reset y cargar desde inicio cuando cambia categoría
    setProductos([]);
    setOffset(0);
    setHayMas(true);
    cargarProductos(0, true);
  }, [categoriaFiltro]);

  // Búsqueda con debounce cuando cambia el filtro de nombre
  useEffect(() => {
    // Limpiar timeout anterior
    if (filtroNombreTimeout.current) {
      clearTimeout(filtroNombreTimeout.current);
    }

    // Si el filtro está vacío, recargar productos normalmente
    if (!filtrosColumna.nombre || filtrosColumna.nombre.length < 2) {
      if (filtrosColumna.nombre === '' && productos.length === 0) {
        // Solo recargar si el filtro se limpió y no hay productos
        setProductos([]);
        setOffset(0);
        setHayMas(true);
        cargarProductos(0, true);
      }
      return;
    }

    // Debounce de 500ms para buscar
    setBuscandoNombre(true);
    filtroNombreTimeout.current = setTimeout(() => {
      buscarPorNombre(filtrosColumna.nombre);
    }, 500);

    return () => {
      if (filtroNombreTimeout.current) {
        clearTimeout(filtroNombreTimeout.current);
      }
    };
  }, [filtrosColumna.nombre]);

  // Detectar scroll para carga progresiva
  useEffect(() => {
    const container = tablaContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (cargandoMas || !hayMas || loading || buscandoNombre) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      // Cargar más cuando estamos a 200px del final
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        cargarMasProductos();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [cargandoMas, hayMas, loading, offset, buscandoNombre, filtrosColumna.nombre]);

  const cargarCategorias = async () => {
    try {
      const result = await apiService.getCategorias();
      if (result.success) {
        setCategorias(result.data || []);
      }
    } catch (error) {
      console.error('Error cargando categorías:', error);
    }
  };

  const cargarProductos = async (nuevoOffset = 0, esReset = false) => {
    try {
      setLoading(true);
      const params = {
        pagina: Math.floor(nuevoOffset / limite) + 1,
        limite,
        categoria_id: categoriaFiltro || undefined,
        busqueda: busqueda || undefined
      };

      // Usar endpoint específico que excluye productos base con equivalencias
      const result = await apiService.getProductosParaPrecios(params);

      if (result.success) {
        const nuevosProductos = result.data.productos || [];
        const total = result.data.paginacion?.total || 0;

        if (esReset) {
          setProductos(nuevosProductos);
          setProductosEditados({});
          setPorcentajes({});
        } else {
          setProductos(prev => [...prev, ...nuevosProductos]);
        }

        setTotalProductos(total);
        setOffset(nuevoOffset + nuevosProductos.length);
        setHayMas(nuevoOffset + nuevosProductos.length < total);
      }
    } catch (error) {
      console.error('Error cargando productos:', error);
      showToast('Error al cargar productos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const cargarMasProductos = async () => {
    if (cargandoMas || !hayMas || buscandoNombre) return;

    try {
      setCargandoMas(true);
      // Usar filtro de nombre si está activo, sino usar búsqueda general
      const terminoBusqueda = filtrosColumna.nombre || busqueda || undefined;
      const params = {
        pagina: Math.floor(offset / limite) + 1,
        limite,
        categoria_id: categoriaFiltro || undefined,
        busqueda: terminoBusqueda
      };

      const result = await apiService.getProductosParaPrecios(params);

      if (result.success) {
        const nuevosProductos = result.data.productos || [];
        const total = result.data.paginacion?.total || 0;

        setProductos(prev => [...prev, ...nuevosProductos]);
        setOffset(prev => prev + nuevosProductos.length);
        setHayMas(offset + nuevosProductos.length < total);
      }
    } catch (error) {
      console.error('Error cargando más productos:', error);
    } finally {
      setCargandoMas(false);
    }
  };

  const buscarProductos = () => {
    setProductos([]);
    setOffset(0);
    setHayMas(true);
    busquedaTrigger.current++;
    cargarProductos(0, true);
  };

  // Buscar productos por nombre en el API (para filtro de columna)
  const buscarPorNombre = async (nombre) => {
    try {
      setBuscandoNombre(true);
      const params = {
        pagina: 1,
        limite: 200, // Traer más resultados para búsqueda
        categoria_id: categoriaFiltro || undefined,
        busqueda: nombre
      };

      const result = await apiService.getProductosParaPrecios(params);

      if (result.success) {
        const nuevosProductos = result.data.productos || [];
        const total = result.data.paginacion?.total || 0;

        setProductos(nuevosProductos);
        setTotalProductos(total);
        setOffset(nuevosProductos.length);
        setHayMas(nuevosProductos.length < total);
        // Limpiar ediciones al hacer nueva búsqueda
        setProductosEditados({});
        setPorcentajes({});
      }
    } catch (error) {
      console.error('Error buscando productos:', error);
      showToast('Error al buscar productos', 'error');
    } finally {
      setBuscandoNombre(false);
    }
  };

  // Calcular precio basado en costo y porcentaje de ganancia
  const calcularPrecioConGanancia = (costo, porcentaje) => {
    if (!costo || costo <= 0 || !porcentaje) return 0;
    return Math.round((costo * (1 + porcentaje / 100)) * 100) / 100;
  };

  // Calcular porcentaje de ganancia basado en costo y precio
  const calcularPorcentajeGanancia = (costo, precio) => {
    if (!costo || costo <= 0 || !precio || precio <= 0) return 0;
    return Math.round(((precio - costo) / costo) * 100 * 100) / 100;
  };

  // Obtener costo actual (editado o original)
  const getCosto = (producto) => {
    if (productosEditados[producto.id]?.precio_compra !== undefined) {
      return productosEditados[producto.id].precio_compra;
    }
    return producto.precio_compra || 0;
  };

  // Manejar cambio de costo
  const handleCostoChange = (productoId, valor) => {
    const valorNumerico = parseFloat(valor) || 0;
    const producto = productos.find(p => p.id === productoId);
    if (!producto) return;

    // Obtener el costo anterior para calcular los porcentajes actuales
    const costoAnterior = getCosto(producto);

    const campos = ['precio_lista', 'precio1', 'precio2', 'precio3', 'precio4', 'precio5', 'precio6'];
    const nuevosPrecios = { precio_compra: valorNumerico };
    const nuevosPorcentajes = {};

    // Recalcular todos los precios basándose en los porcentajes actuales
    if (valorNumerico > 0) {
      campos.forEach(campo => {
        // Obtener el porcentaje actual (editado o calculado)
        let porcentajeActual;

        if (porcentajes[productoId]?.[campo] !== undefined) {
          // Usar porcentaje editado manualmente
          porcentajeActual = parseFloat(porcentajes[productoId][campo]);
        } else {
          // Calcular porcentaje basado en costo anterior y precio actual
          const precioActual = getValor(producto, campo);
          if (costoAnterior > 0 && precioActual > 0) {
            porcentajeActual = calcularPorcentajeGanancia(costoAnterior, precioActual);
          } else {
            porcentajeActual = 0;
          }
        }

        // Guardar el porcentaje original para mantenerlo
        nuevosPorcentajes[campo] = porcentajeActual.toString();

        // Calcular nuevo precio con el porcentaje actual
        if (!isNaN(porcentajeActual) && porcentajeActual > 0) {
          nuevosPrecios[campo] = calcularPrecioConGanancia(valorNumerico, porcentajeActual);
        }
      });
    }

    // Guardar los porcentajes para que no se recalculen y pierdan decimales
    setPorcentajes(prev => ({
      ...prev,
      [productoId]: {
        ...prev[productoId],
        ...nuevosPorcentajes
      }
    }));

    setProductosEditados(prev => ({
      ...prev,
      [productoId]: {
        ...prev[productoId],
        ...nuevosPrecios
      }
    }));
  };

  // Manejar cambio de porcentaje (calcula precio automáticamente)
  const handlePorcentajeChange = (productoId, campo, valor) => {
    const porcentaje = parseFloat(valor);

    // Guardar porcentaje temporal
    setPorcentajes(prev => ({
      ...prev,
      [productoId]: {
        ...prev[productoId],
        [campo]: valor
      }
    }));

    // Calcular y guardar precio si el porcentaje es válido
    if (!isNaN(porcentaje)) {
      const producto = productos.find(p => p.id === productoId);
      const costo = getCosto(producto);

      if (costo > 0) {
        const nuevoPrecio = calcularPrecioConGanancia(costo, porcentaje);
        setProductosEditados(prev => ({
          ...prev,
          [productoId]: {
            ...prev[productoId],
            [campo]: nuevoPrecio
          }
        }));
      }
    }
  };

  // Manejar cambio de precio directo (calcula porcentaje automáticamente)
  const handlePrecioChange = (productoId, campo, valor) => {
    const valorNumerico = parseFloat(valor) || 0;

    setProductosEditados(prev => ({
      ...prev,
      [productoId]: {
        ...prev[productoId],
        [campo]: valorNumerico
      }
    }));

    // Calcular y mostrar porcentaje de ganancia
    const producto = productos.find(p => p.id === productoId);
    const costo = getCosto(producto);

    if (costo > 0 && valorNumerico > 0) {
      const porcentaje = calcularPorcentajeGanancia(costo, valorNumerico);
      setPorcentajes(prev => ({
        ...prev,
        [productoId]: {
          ...prev[productoId],
          [campo]: porcentaje.toString()
        }
      }));
    }
  };

  // Manejar cambio de cantidad descuento
  const handleDescuentoChange = (productoId, valor) => {
    const valorNumerico = parseFloat(valor) || 0;

    setProductosEditados(prev => ({
      ...prev,
      [productoId]: {
        ...prev[productoId],
        cantidad_descuento: valorNumerico
      }
    }));
  };

  // Manejar cambio de porcentaje descuento (se aplica a precio_lista)
  const handlePorcentajeDescuentoChange = (productoId, valor) => {
    const valorNumerico = parseFloat(valor) || 0;

    setProductosEditados(prev => ({
      ...prev,
      [productoId]: {
        ...prev[productoId],
        porcentaje_descuento: valorNumerico
      }
    }));
  };

  // Obtener valor actual (editado o original)
  const getValor = (producto, campo) => {
    if (productosEditados[producto.id]?.[campo] !== undefined) {
      return productosEditados[producto.id][campo];
    }
    return producto[campo] || 0;
  };

  // Obtener porcentaje (temporal si fue editado, o calculado automáticamente)
  const getPorcentaje = (productoId, campo) => {
    // Si hay un porcentaje editado manualmente, usarlo
    if (porcentajes[productoId]?.[campo] !== undefined) {
      return porcentajes[productoId][campo];
    }

    // Calcular automáticamente basado en costo y precio actual
    const producto = productos.find(p => p.id === productoId);
    if (!producto) return '';

    const costo = getCosto(producto);
    const precio = getValor(producto, campo);

    if (costo > 0 && precio > 0) {
      const porcentajeCalculado = calcularPorcentajeGanancia(costo, precio);
      return porcentajeCalculado.toString();
    }

    return '';
  };

  // Verificar si un producto fue editado
  const fueEditado = (productoId) => {
    return productosEditados[productoId] !== undefined;
  };

  // Guardar cambios de un producto
  const guardarProducto = async (productoId) => {
    const cambios = productosEditados[productoId];
    if (!cambios) return;

    try {
      setGuardando(true);

      // Separar cambios de precios y de descuento
      const preciosCambios = {};
      let cantidadDescuento = null;
      let porcentajeDescuento = null;

      Object.keys(cambios).forEach(key => {
        if (key === 'cantidad_descuento') {
          cantidadDescuento = cambios[key];
        } else if (key === 'porcentaje_descuento') {
          porcentajeDescuento = cambios[key];
        } else {
          preciosCambios[key] = cambios[key];
        }
      });

      // Actualizar precios si hay cambios
      if (Object.keys(preciosCambios).length > 0) {
        const result = await apiService.actualizarPrecios(productoId, preciosCambios);
        if (!result.success) {
          showToast(result.message || 'Error al actualizar precios', 'error');
          return;
        }
      }

      // Actualizar descuento por volumen si cambió cantidad o porcentaje
      if (cantidadDescuento !== null || porcentajeDescuento !== null) {
        const producto = productos.find(p => p.id === productoId);
        const cantidadFinal = cantidadDescuento !== null ? cantidadDescuento : (producto?.cantidad_descuento || 0);
        // Siempre enviar el porcentaje (el editado o el actual del producto)
        const porcentajeFinal = porcentajeDescuento !== null ? porcentajeDescuento : (producto?.porcentaje_descuento || 0);
        await apiService.updateDescuentoVolumen(productoId, cantidadFinal, porcentajeFinal);
      }

      // Limpiar cambios del producto
      setProductosEditados(prev => {
        const nuevo = { ...prev };
        delete nuevo[productoId];
        return nuevo;
      });

      // Limpiar porcentajes del producto
      setPorcentajes(prev => {
        const nuevo = { ...prev };
        delete nuevo[productoId];
        return nuevo;
      });

      // Actualizar producto en la lista
      setProductos(prev => prev.map(p => {
        if (p.id === productoId) {
          return {
            ...p,
            ...preciosCambios,
            cantidad_descuento: cantidadDescuento ?? p.cantidad_descuento,
            porcentaje_descuento: porcentajeDescuento ?? p.porcentaje_descuento
          };
        }
        return p;
      }));

      showToast('Producto actualizado', 'success');
    } catch (error) {
      console.error('Error guardando:', error);
      showToast('Error al guardar cambios', 'error');
    } finally {
      setGuardando(false);
    }
  };

  // Guardar todos los cambios
  const guardarTodos = async () => {
    const productosConCambios = Object.keys(productosEditados);
    if (productosConCambios.length === 0) {
      showToast('No hay cambios para guardar', 'info');
      return;
    }

    setGuardando(true);
    let guardados = 0;
    let errores = 0;

    for (const productoId of productosConCambios) {
      try {
        await guardarProducto(parseInt(productoId));
        guardados++;
      } catch (error) {
        errores++;
      }
    }

    setGuardando(false);

    if (errores === 0) {
      showToast(`${guardados} productos actualizados`, 'success');
    } else {
      showToast(`${guardados} guardados, ${errores} errores`, 'warning');
    }
  };

  // Cancelar cambios de un producto
  const cancelarCambios = (productoId) => {
    setProductosEditados(prev => {
      const nuevo = { ...prev };
      delete nuevo[productoId];
      return nuevo;
    });
    setPorcentajes(prev => {
      const nuevo = { ...prev };
      delete nuevo[productoId];
      return nuevo;
    });
  };

  // Cancelar todos los cambios
  const cancelarTodos = () => {
    setProductosEditados({});
    setPorcentajes({});
  };

  // Aplicar porcentaje a todos los precios de un producto
  const aplicarPorcentajeATodos = (productoId, porcentajeGlobal) => {
    const producto = productos.find(p => p.id === productoId);
    const costo = getCosto(producto);

    if (costo <= 0 || !porcentajeGlobal) return;

    const campos = ['precio_lista', 'precio1', 'precio2', 'precio3', 'precio4', 'precio5', 'precio6'];
    const nuevosPrecios = {};
    const nuevosPorcentajes = {};

    campos.forEach(campo => {
      nuevosPrecios[campo] = calcularPrecioConGanancia(costo, porcentajeGlobal);
      nuevosPorcentajes[campo] = porcentajeGlobal.toString();
    });

    setProductosEditados(prev => ({
      ...prev,
      [productoId]: {
        ...prev[productoId],
        ...nuevosPrecios
      }
    }));

    setPorcentajes(prev => ({
      ...prev,
      [productoId]: {
        ...prev[productoId],
        ...nuevosPorcentajes
      }
    }));
  };

  const cantidadEditados = Object.keys(productosEditados).length;

  // Filtrar productos por filtros de columna (solo código es local, nombre usa API)
  const productosFiltrados = productos.filter(producto => {
    // Filtro por código (local)
    if (filtrosColumna.codigo && !producto.codigo_corto?.toLowerCase().includes(filtrosColumna.codigo.toLowerCase())) {
      return false;
    }
    return true;
  });

  // Actualizar filtro de columna
  const handleFiltroColumna = (campo, valor) => {
    setFiltrosColumna(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  // Limpiar todos los filtros de columna
  const limpiarFiltrosColumna = () => {
    const teniaFiltroNombre = filtrosColumna.nombre !== '';
    setFiltrosColumna({
      codigo: '',
      nombre: ''
    });
    // Si había filtro de nombre, recargar productos desde el API
    if (teniaFiltroNombre) {
      setProductos([]);
      setOffset(0);
      setHayMas(true);
      setTimeout(() => cargarProductos(0, true), 100);
    }
  };

  // Verificar si hay filtros activos
  const hayFiltrosActivos = Object.values(filtrosColumna).some(v => v !== '');

  // Campos de precios para renderizar
  const camposPrecios = [
    { key: 'precio_lista', label: 'P.Lista' },
    { key: 'precio1', label: 'Precio 1' },
    { key: 'precio2', label: 'Precio 2' },
    { key: 'precio3', label: 'Precio 3' },
    { key: 'precio4', label: 'Precio 4' },
    { key: 'precio5', label: 'Precio 5' },
    { key: 'precio6', label: 'Precio 6' }
  ];

  // Columnas para navegación tipo Excel
  const columnasNavegacion = [
    'costo',
    'cantidad_descuento',
    'porcentaje_descuento',
    ...camposPrecios.flatMap(c => [`${c.key}_pct`, `${c.key}_precio`])
  ];

  // Navegación tipo Excel
  const handleKeyNavigation = (e, rowIndex, colName) => {
    const currentColIndex = columnasNavegacion.indexOf(colName);
    let targetRow = rowIndex;
    let targetCol = currentColIndex;

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        targetRow = Math.max(0, rowIndex - 1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        targetRow = Math.min(productosFiltrados.length - 1, rowIndex + 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        targetCol = Math.max(0, currentColIndex - 1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        targetCol = Math.min(columnasNavegacion.length - 1, currentColIndex + 1);
        break;
      case 'Tab':
        e.preventDefault();
        if (e.shiftKey) {
          // Shift+Tab: retroceder
          if (currentColIndex > 0) {
            targetCol = currentColIndex - 1;
          } else if (rowIndex > 0) {
            targetRow = rowIndex - 1;
            targetCol = columnasNavegacion.length - 1;
          }
        } else {
          // Tab: avanzar
          if (currentColIndex < columnasNavegacion.length - 1) {
            targetCol = currentColIndex + 1;
          } else if (rowIndex < productosFiltrados.length - 1) {
            targetRow = rowIndex + 1;
            targetCol = 0;
          }
        }
        break;
      case 'Enter':
        e.preventDefault();
        targetRow = Math.min(productosFiltrados.length - 1, rowIndex + 1);
        break;
      default:
        return;
    }

    // Buscar y enfocar el input objetivo
    const targetColName = columnasNavegacion[targetCol];
    const targetInput = document.querySelector(
      `input[data-row="${targetRow}"][data-col="${targetColName}"]`
    );
    if (targetInput) {
      targetInput.focus();
      targetInput.select();
    }
  };

  return (
    <div className="cambio-precios-page">
      {/* Filtros */}
      <div className="filtros-container">
        <div className="filtro-grupo">
          <label><Filter size={14} /> Categoria</label>
          <select
            value={categoriaFiltro}
            onChange={(e) => setCategoriaFiltro(e.target.value)}
          >
            <option value="">Todas las categorias</option>
            {categorias.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.nombre}</option>
            ))}
          </select>
        </div>

        <div className="filtro-grupo busqueda">
          <label><Search size={14} /> Buscar</label>
          <div className="busqueda-input">
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && buscarProductos()}
              placeholder="Codigo o descripcion..."
            />
            <Button variant="primary" onClick={buscarProductos} disabled={loading}>
              <Search size={16} />
            </Button>
          </div>
        </div>

        <div className="filtro-grupo">
          <label>&nbsp;</label>
          <Button variant="secondary" onClick={() => { setBusqueda(''); setCategoriaFiltro(''); setFiltrosColumna({ codigo: '', nombre: '' }); setProductos([]); setOffset(0); setHayMas(true); cargarProductos(0, true); }}>
            <RefreshCw size={16} />
            Limpiar
          </Button>
        </div>

        {cantidadEditados > 0 && (
          <div className="filtro-grupo acciones-pendientes">
            <label>&nbsp;</label>
            <div className="acciones-btns">
              <span className="cambios-pendientes">
                <AlertCircle size={14} />
                {cantidadEditados}
              </span>
              <Button variant="secondary" size="sm" onClick={cancelarTodos} disabled={guardando}>
                Cancelar
              </Button>
              <Button variant="success" size="sm" onClick={guardarTodos} disabled={guardando}>
                {guardando ? <RefreshCw size={14} className="spin" /> : null}
                Guardar
              </Button>
            </div>
          </div>
        )}
      </div>



      {/* Info */}
      <div className="info-bar">
        <span>
          {buscandoNombre ? (
            <>
              <RefreshCw size={12} className="spin" style={{ marginRight: '4px' }} />
              Buscando "{filtrosColumna.nombre}"...
            </>
          ) : (
            <>
              Mostrando {productosFiltrados.length} de {productos.length} productos cargados (Total: {totalProductos})
              {filtrosColumna.nombre && <span className="filtro-activo-badge">Filtrado por: "{filtrosColumna.nombre}"</span>}
            </>
          )}
          {hayFiltrosActivos && (
            <button className="btn-limpiar-filtros" onClick={limpiarFiltrosColumna} title="Limpiar filtros">
              <X size={12} /> Limpiar filtros
            </button>
          )}
        </span>
        {hayMas && !buscandoNombre && <span className="mas-productos">Desplaza para cargar más...</span>}
      </div>

      {/* Scrollbar superior */}
      <div className="top-scroll-wrapper" ref={topScrollRef}>
        <div className="top-scroll-content"></div>
      </div>

      {/* Tabla de precios */}
      <div className="tabla-precios-container" ref={tablaContainerRef}>
        <table className="tabla-precios" ref={tablaRef}>
          <thead>
            <tr>
              <th className="col-codigo">Codigo</th>
              <th className="col-descripcion">Descripcion</th>
              <th className="col-costo">Costo</th>
              <th className="col-cantidad">Cant.Desc</th>
              <th className="col-porcentaje-desc">%Desc</th>
              {camposPrecios.map(campo => (
                <th key={campo.key} className="col-precio-doble">
                  <div className="header-precio">
                    <span>{campo.label}</span>
                    <small>% / $</small>
                  </div>
                </th>
              ))}
              <th className="col-acciones">


                Acc.</th>
            </tr>
            {/* Fila de filtros tipo Excel */}
            <tr className="fila-filtros">
              <th className="col-codigo">
                <input
                  type="text"
                  value={filtrosColumna.codigo}
                  onChange={(e) => handleFiltroColumna('codigo', e.target.value)}
                  placeholder="Filtrar..."
                  className="filtro-input"
                />
              </th>
              <th className="col-descripcion">
                <input
                  type="text"
                  value={filtrosColumna.nombre}
                  onChange={(e) => handleFiltroColumna('nombre', e.target.value)}
                  placeholder="Filtrar..."
                  className="filtro-input"
                />
              </th>
              <th className="col-costo"></th>
              <th className="col-cantidad"></th>
              <th className="col-porcentaje-desc"></th>
              {camposPrecios.map(campo => (
                <th key={`filtro-${campo.key}`} className="col-precio-doble"></th>
              ))}
              <th className="col-acciones">
                {hayFiltrosActivos && (
                  <button className="btn-accion cancelar" onClick={limpiarFiltrosColumna} title="Limpiar filtros">
                    <X size={12} />
                  </button>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {productosFiltrados.map((producto, rowIndex) => (
              <tr key={producto.id} className={fueEditado(producto.id) ? 'editado' : ''}>
                <td className="col-codigo">{producto.codigo_corto || '-'}</td>
                <td className="col-descripcion" title={producto.nombre}>
                  {producto.nombre?.substring(0, 45)}
                  {producto.nombre?.length > 45 ? '...' : ''}
                </td>
                <td className="col-costo">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={getValor(producto, 'precio_compra')}
                    onChange={(e) => handleCostoChange(producto.id, e.target.value)}
                    onKeyDown={(e) => handleKeyNavigation(e, rowIndex, 'costo')}
                    onFocus={(e) => e.target.select()}
                    className="input-costo"
                    data-row={rowIndex}
                    data-col="costo"
                  />
                </td>
                <td className="col-cantidad">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={getValor(producto, 'cantidad_descuento')}
                    onChange={(e) => handleDescuentoChange(producto.id, e.target.value)}
                    onKeyDown={(e) => handleKeyNavigation(e, rowIndex, 'cantidad_descuento')}
                    onFocus={(e) => e.target.select()}
                    className="input-cantidad"
                    data-row={rowIndex}
                    data-col="cantidad_descuento"
                  />
                </td>
                <td className="col-porcentaje-desc">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={getValor(producto, 'porcentaje_descuento')}
                    onChange={(e) => handlePorcentajeDescuentoChange(producto.id, e.target.value)}
                    onKeyDown={(e) => handleKeyNavigation(e, rowIndex, 'porcentaje_descuento')}
                    onFocus={(e) => e.target.select()}
                    className="input-porcentaje-desc"
                    placeholder="%"
                    title="% descuento sobre P.Lista"
                    data-row={rowIndex}
                    data-col="porcentaje_descuento"
                  />
                </td>
                {camposPrecios.map(campo => (
                  <td key={campo.key} className="col-precio-doble">
                    <div className="precio-doble-input">
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={getPorcentaje(producto.id, campo.key)}
                        onChange={(e) => handlePorcentajeChange(producto.id, campo.key, e.target.value)}
                        onKeyDown={(e) => handleKeyNavigation(e, rowIndex, `${campo.key}_pct`)}
                        onFocus={(e) => e.target.select()}
                        className="input-porcentaje"
                        placeholder="%"
                        title="Porcentaje de ganancia"
                        data-row={rowIndex}
                        data-col={`${campo.key}_pct`}
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={getValor(producto, campo.key)}
                        onChange={(e) => handlePrecioChange(producto.id, campo.key, e.target.value)}
                        onKeyDown={(e) => handleKeyNavigation(e, rowIndex, `${campo.key}_precio`)}
                        onFocus={(e) => e.target.select()}
                        className="input-precio"
                        title="Precio final"
                        data-row={rowIndex}
                        data-col={`${campo.key}_precio`}
                      />
                    </div>
                  </td>
                ))}
                <td className="col-acciones">
                  {fueEditado(producto.id) && (
                    <>
                      <button
                        className="btn-accion guardar"
                        onClick={() => guardarProducto(producto.id)}
                        disabled={guardando}
                        title="Guardar"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        className="btn-accion cancelar"
                        onClick={() => cancelarCambios(producto.id)}
                        disabled={guardando}
                        title="Cancelar"
                      >
                        <X size={14} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {productosFiltrados.length === 0 && !loading && (
          <div className="sin-resultados">
            <AlertCircle size={48} />
            <p>{hayFiltrosActivos ? 'No hay productos que coincidan con los filtros' : 'No se encontraron productos'}</p>
            {hayFiltrosActivos && (
              <Button variant="secondary" onClick={limpiarFiltrosColumna}>
                Limpiar filtros
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Indicador de carga progresiva */}
      {cargandoMas && (
        <div className="cargando-mas">
          <RefreshCw size={16} className="spin" />
          <span>Cargando más productos...</span>
        </div>
      )}

      {!hayMas && productos.length > 0 && (
        <div className="fin-lista">
          <span>— Fin de la lista ({totalProductos} productos) —</span>
        </div>
      )}
    </div>
  );
};

export default CambioPreciosPage;
